import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Phone,
  MessageSquare,
  ExternalLink,
  Plus,
  MapPin,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import {
  api,
  type Customer,
  type CustomerContact,
  type CustomerShipTo,
  type SalesOrderSummary,
} from '../lib/api.js';
import { normalizeTrade, useSalesTypes, type Trade } from '../lib/trade.js';
import { TradePill } from '../components/ui/trade-pill.js';

/**
 * Customer 360 — redesigned against Design/hvac_customer_360.html.
 *
 * What we surface from real data:
 *   - Identity header + status pills (active/hold/trade/sell level)
 *   - KPI tiles: this-year sales, AR balance, open-orders total, last invoice date
 *   - Open quotes & orders list (search by customerNo, type=Q + W)
 *   - Billing + shipping addresses, credit terms, primary contact
 *
 * Sections that lack a data source today (equipment, service contracts,
 * service history, photos, AR aging buckets) are present as clearly-marked
 * Phase 6 placeholders so the layout still matches the mockup.
 */
export function CustomersDetailScreen() {
  const { id } = useParams({ from: '/customers/$id' });
  const spireId = Number(id);

  const customerQuery = useQuery({
    queryKey: ['customer', spireId],
    queryFn: ({ signal }) => api.customers.getById(spireId, signal),
    enabled: Number.isFinite(spireId),
  });

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/customers"
          aria-label="Back to customers"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>Customers</span>
        </Link>
      </div>

      {customerQuery.isError && (
        <QueryError
          title={
            customerQuery.error.kind === 'not_found'
              ? 'Customer not found'
              : "Couldn't load customer"
          }
          error={customerQuery.error}
          onRetry={() => void customerQuery.refetch()}
        />
      )}

      {customerQuery.isPending && <DetailSkeleton />}
      {customerQuery.data && <Body customer={customerQuery.data} />}
    </div>
  );
}

function Body({ customer }: { customer: Customer }) {
  const types = useSalesTypes();
  const trade = normalizeTrade(types, customer.userDef2);

  return (
    <>
      <HeaderRow customer={customer} trade={trade} />
      <KpiRow customer={customer} />
      <OrdersSection customerNo={customer.customerNo} />

      <div className="grid gap-3 md:grid-cols-2">
        <ContactCard customer={customer} />
        <CreditCard customer={customer} />
      </div>

      <ShipTosCard customer={customer} />

      {/* Planned sections (Equipment on site, Service contracts, Photo
          evidence) were here. Hidden until the underlying surfaces ship —
          see git history if you need the explanatory copy. */}
    </>
  );
}

function HeaderRow({ customer, trade }: { customer: Customer; trade: Trade }) {
  const types = useSalesTypes();
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3 flex-wrap">
        <CustomerAvatar name={customer.name} />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs text-ink-muted">{customer.customerNo}</div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink truncate">
            {customer.name}
          </h1>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {customer.status === 'active' && <Pill variant="success" dot>Active</Pill>}
            {customer.hold && <Pill variant="danger">On hold</Pill>}
            <TradePill types={types} trade={trade} hideUnknown />
            <Pill variant="neutral">Sell level {customer.sellLevel}</Pill>
            {customer.defaultWarehouse && (
              <Pill variant="neutral">Whse {customer.defaultWarehouse}</Pill>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {customer.address.phone && (
            <a
              href={`tel:${customer.address.phone}`}
              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs bg-surface hover:bg-surface-muted"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <Phone className="h-3.5 w-3.5" aria-hidden />
              Call
            </a>
          )}
          {customer.address.phone && (
            <a
              href={`sms:${customer.address.phone}`}
              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs bg-surface hover:bg-surface-muted"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden />
              SMS
            </a>
          )}
          <Link
            to="/quote-builder"
            search={{ customerNo: customer.customerNo }}
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-white"
            style={{ backgroundColor: 'var(--color-text-info)' }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New quote
          </Link>
        </div>
      </div>

      <div className="text-xs text-ink-muted">
        {formatAddress(customer)} {customer.address.phone && `· ${customer.address.phone}`}{' '}
        {customer.address.email && `· ${customer.address.email}`}{' '}
        · Spire customerNo <span className="font-mono">{customer.customerNo}</span>
      </div>

      {customer.userDef1 && (
        <div className="text-xs italic text-ink-subtle">{customer.userDef1}</div>
      )}
    </div>
  );
}

function KpiRow({ customer }: { customer: Customer }) {
  const arBalance = Number(customer.creditBalance);
  const hasArDue = arBalance > 0;
  return (
    <div className="grid gap-2 grid-cols-2 md:grid-cols-4">
      <Kpi
        label="This year"
        value={`$${formatThousands(customer.thisYearSales)}`}
        sub="Sales YTD"
      />
      <Kpi
        label="AR balance"
        value={`$${formatThousands(customer.creditBalance)}`}
        sub={hasArDue ? 'Outstanding' : 'Paid up'}
        accent={hasArDue ? 'danger' : 'success'}
      />
      <Kpi
        label="Open orders"
        value={`$${formatThousands(customer.openOrdersTotal)}`}
        sub="Pending in Spire"
      />
      <Kpi
        label="Last invoice"
        value={customer.lastInvoiceDate ?? '—'}
        sub={customer.lastInvoiceDate ? relativeDate(customer.lastInvoiceDate) : 'No history'}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: 'danger' | 'success';
}) {
  const valueColor =
    accent === 'danger'
      ? 'text-red-900'
      : accent === 'success'
        ? 'text-emerald-700'
        : 'text-ink';
  return (
    <div
      className="rounded-lg border bg-surface px-3 py-2.5"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${valueColor}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-subtle mt-0.5">{sub}</div>}
    </div>
  );
}

function OrdersSection({ customerNo }: { customerNo: string }) {
  const query = useQuery({
    queryKey: ['customer-orders', customerNo],
    queryFn: ({ signal }) =>
      api.salesOrders.search({ customerNo, limit: 20 }, signal),
    enabled: Boolean(customerNo),
    staleTime: 30_000,
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2 pb-2">
        <CardTitle>Quotes & orders</CardTitle>
        {query.data && query.data.length > 0 && (
          <span className="text-xs text-ink-muted">{query.data.length} total</span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        {query.isPending && (
          <div className="p-3">
            <div className="space-y-1.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-9 bg-surface-muted rounded animate-pulse" />
              ))}
            </div>
          </div>
        )}
        {query.isError && (
          <div className="p-3">
            <QueryError
              title="Couldn't load quotes & orders"
              error={query.error}
              onRetry={() => void query.refetch()}
              size="md"
            />
          </div>
        )}
        {query.data && query.data.length === 0 && (
          <div className="p-3 text-sm text-ink-muted">
            No quotes or orders for this customer yet.
          </div>
        )}
        {query.data && query.data.length > 0 && <OrdersTable orders={query.data} />}
      </CardBody>
    </Card>
  );
}

function OrdersTable({ orders }: { orders: ReadonlyArray<SalesOrderSummary> }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr
          className="text-left text-[10px] uppercase tracking-wider text-ink-subtle border-b"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          <th className="px-3 py-2 font-normal">#</th>
          <th className="px-2 py-2 font-normal">Type</th>
          <th className="px-2 py-2 font-normal">Status</th>
          <th className="px-2 py-2 font-normal">Required</th>
          <th className="px-2 py-2 font-normal text-right">Total</th>
          <th className="px-2 py-2 font-normal" />
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr
            key={o.spireId}
            className="border-b hover:bg-surface-muted"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <td className="px-3 py-2 font-mono text-xs text-ink">{o.orderNo}</td>
            <td className="px-2 py-2"><TypePill type={o.type} /></td>
            <td className="px-2 py-2"><StatusPill status={o.status} /></td>
            <td className="px-2 py-2 text-xs text-ink-muted">{o.requiredDate ?? '—'}</td>
            <td className="px-2 py-2 text-right tabular-nums">${o.total}</td>
            <td className="px-2 py-2 text-right">
              <Link
                to="/sales-orders/$id"
                params={{ id: String(o.spireId) }}
                className="inline-flex items-center gap-1 text-xs text-ink-accent hover:underline"
              >
                Open
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TypePill({ type }: { type: SalesOrderSummary['type'] }) {
  if (type === 'quote') return <Pill variant="info">Quote</Pill>;
  if (type === 'order') return <Pill variant="install">Order</Pill>;
  return <Pill variant="neutral">{type}</Pill>;
}

function StatusPill({ status }: { status: SalesOrderSummary['status'] }) {
  if (status === 'open') return <Pill variant="success" dot>Open</Pill>;
  if (status === 'invoiced') return <Pill variant="neutral">Invoiced</Pill>;
  if (status === 'cancelled') return <Pill variant="danger">Cancelled</Pill>;
  return <Pill variant="neutral">{status}</Pill>;
}

function ContactCard({ customer }: { customer: Customer }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact &amp; address</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Billing address</div>
          <div className="text-ink">{customer.address.line1 ?? '—'}</div>
          {customer.address.line2 && <div className="text-ink-muted">{customer.address.line2}</div>}
          <div className="text-ink-muted">
            {[customer.address.city, customer.address.provState, customer.address.postalCode]
              .filter(Boolean)
              .join(' ') || '—'}
          </div>
        </div>
        {(customer.address.phone || customer.address.email || customer.website) && (
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Reach</div>
            {customer.address.phone && <PhoneLink phone={customer.address.phone} />}
            {customer.address.email && <EmailLink email={customer.address.email} />}
            {customer.website && (
              <div className="text-xs">
                <a
                  href={customer.website.startsWith('http') ? customer.website : `https://${customer.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ink-accent hover:underline"
                >
                  {customer.website}
                </a>
              </div>
            )}
          </div>
        )}
        {customer.contacts.length > 0 ? (
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Contacts ({customer.contacts.length})
            </div>
            <ul className="space-y-2">
              {customer.contacts.map((c, i) => (
                <ContactRow key={`${c.name ?? ''}-${i}`} contact={c} />
              ))}
            </ul>
          </div>
        ) : customer.primaryContact ? (
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Primary contact</div>
            <ul>
              <ContactRow contact={customer.primaryContact} />
            </ul>
          </div>
        ) : null}
        {(customer.salespersonName || customer.salesperson || customer.territory) && (
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Account</div>
            {(customer.salespersonName || customer.salesperson) && (
              <div className="text-xs text-ink-muted">
                Salesperson: {customer.salespersonName ?? customer.salesperson}
              </div>
            )}
            {customer.territory && <div className="text-xs text-ink-muted">Territory: {customer.territory}</div>}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function CreditCard({ customer }: { customer: Customer }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit &amp; terms</CardTitle>
      </CardHeader>
      <CardBody className="space-y-1.5 text-sm">
        <Row
          label="Credit limit"
          value={customer.creditLimit ? `$${formatThousands(customer.creditLimit)}` : '—'}
        />
        <Row label="Balance" value={`$${formatThousands(customer.creditBalance)}`} />
        <Row
          label="Payment terms"
          value={
            customer.paymentTermsCode
              ? `${customer.paymentTermsCode}${customer.paymentTermsDescription ? ` — ${customer.paymentTermsDescription}` : ''}`
              : '—'
          }
        />
        <Row label="Discount" value={`${customer.discount}%`} />
        {customer.currency && <Row label="Currency" value={customer.currency} />}
        {customer.creditApprovedBy && (
          <Row
            label="Credit approved"
            value={`${customer.creditApprovedBy}${customer.creditApprovedDate ? ` · ${customer.creditApprovedDate}` : ''}`}
          />
        )}
        {customer.reference && <Row label="Reference" value={customer.reference} />}
        {customer.createdAt && <Row label="Customer since" value={customer.createdAt.slice(0, 10)} />}
      </CardBody>
    </Card>
  );
}


/**
 * Every ship-to on file. In Spire the ship-to is the SERVICE address (see
 * CLAUDE.md "Job site = the Spire ship-to"), so for a builder or house
 * account this is the list of sites the customer actually has work at.
 */
function ShipTosCard({ customer }: { customer: Customer }) {
  const shipTos = customer.shipTos;
  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle>Ship-to / service addresses</CardTitle>
        <span className="text-xs text-ink-muted">{shipTos.length}</span>
      </CardHeader>
      <CardBody className="p-0">
        {shipTos.length === 0 ? (
          <div className="px-4 py-3 text-sm text-ink-muted">
            No ship-to addresses on file — orders ship to the billing address.
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            {shipTos.map((s) => (
              <ShipToRow key={s.shipId ?? `${s.name}-${s.line1}`} shipTo={s} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ShipToRow({ shipTo }: { shipTo: CustomerShipTo }) {
  const lines = [
    shipTo.line1,
    shipTo.line2,
    shipTo.line3,
    shipTo.line4,
    [shipTo.city, shipTo.provState].filter(Boolean).join(', ') || null,
    shipTo.postalCode,
  ].filter((l): l is string => !!l && l.trim().length > 0);
  const mapsQuery = lines.length > 0 ? lines.join(', ') : null;
  return (
    <li className="px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-ink">{shipTo.name || shipTo.shipId || 'Ship-to'}</span>
            {shipTo.shipId && <span className="font-mono text-xs text-ink-muted">{shipTo.shipId}</span>}
            {shipTo.isDefault && <Pill variant="info">Default</Pill>}
          </div>
          <div className="mt-0.5 text-ink-muted">{lines.join(' · ') || '—'}</div>
          {(shipTo.phone || shipTo.email) && (
            <div className="mt-1 space-y-0.5">
              {shipTo.phone && <PhoneLink phone={shipTo.phone} />}
              {shipTo.email && <EmailLink email={shipTo.email} />}
            </div>
          )}
          {shipTo.contacts.length > 0 && (
            <ul className="mt-2 space-y-1.5 border-l-2 pl-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              {shipTo.contacts.map((c, i) => (
                <ContactRow key={`${c.name ?? ''}-${i}`} contact={c} />
              ))}
            </ul>
          )}
          {(shipTo.defaultWarehouse || shipTo.salesperson) && (
            <div className="mt-1 text-[11px] text-ink-subtle">
              {[
                shipTo.defaultWarehouse ? `Whse ${shipTo.defaultWarehouse}` : null,
                shipTo.sellLevel ? `Level ${shipTo.sellLevel}` : null,
                shipTo.salesperson ? `Salesperson ${shipTo.salesperson}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
        {mapsQuery && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-muted hover:text-ink"
            aria-label={`Open ${shipTo.name ?? 'ship-to'} in maps`}
          >
            <MapPin className="h-4 w-4" aria-hidden />
          </a>
        )}
      </div>
    </li>
  );
}

function ContactRow({ contact }: { contact: CustomerContact }) {
  return (
    <li className="text-sm list-none">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-ink">{contact.name || '—'}</span>
        {contact.contactType && <span className="text-xs text-ink-muted">{contact.contactType}</span>}
      </div>
      {contact.phone && <PhoneLink phone={contact.phone} />}
      {contact.email && <EmailLink email={contact.email} />}
    </li>
  );
}

function PhoneLink({ phone }: { phone: string }) {
  return (
    <div className="text-xs">
      <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="text-ink-muted hover:text-ink hover:underline">
        {formatPhone(phone)}
      </a>
    </div>
  );
}

function EmailLink({ email }: { email: string }) {
  return (
    <div className="text-xs">
      <a href={`mailto:${email}`} className="text-ink-accent hover:underline break-all">
        {email}
      </a>
    </div>
  );
}

/** "+17054924718" → "(705) 492-4718"; anything else is shown as-is. */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return raw;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-ink font-mono">{value}</span>
    </div>
  );
}

function CustomerAvatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  return (
    <span
      aria-hidden
      className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold"
      style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
    >
      {initials}
    </span>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <div className="h-4 w-20 bg-surface-muted rounded animate-pulse" />
      <div className="h-8 w-2/3 bg-surface-muted rounded animate-pulse" />
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 bg-surface-muted rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}


function formatAddress(customer: Customer): string {
  const parts = [
    customer.address.line1,
    customer.address.city,
    customer.address.provState,
    customer.address.postalCode,
  ].filter(Boolean);
  return parts.join(', ');
}

function formatThousands(value: string | null | undefined): string {
  if (!value) return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function relativeDate(iso: string): string {
  // Cheap relative — just days-ago. Good enough for the KPI sub-line.
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

